import { useCallback, useState } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { Button, Input } from '@/components/atoms';
import { BULK_UPLOAD_MAX_FILE_SIZE_MB } from '@/lib/constants/bulk-upload-template';
import { cn } from '@/lib/utils';
import { FileSpreadsheet, UploadCloud, X } from 'lucide-react';

interface BulkUploadDropzoneProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
}

const ACCEPTED_TYPES = {
  'text/csv': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
  'application/vnd.ms-excel': ['.xls'],
};

export function BulkUploadDropzone({
  file,
  onFileChange,
  disabled = false,
}: BulkUploadDropzoneProps) {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        const rejection = fileRejections[0];
        const tooLarge = rejection.errors.some(
          (entry) => entry.code === 'file-too-large'
        );
        const invalidType = rejection.errors.some(
          (entry) => entry.code === 'file-invalid-type'
        );

        if (tooLarge) {
          setError(`Maximum file size is ${BULK_UPLOAD_MAX_FILE_SIZE_MB}MB.`);
        } else if (invalidType) {
          setError('Supported file formats: CSV and Excel (.xlsx).');
        } else {
          setError('Unable to use this file. Please try another one.');
        }

        onFileChange(null);
        return;
      }

      setError(null);
      onFileChange(acceptedFiles[0] ?? null);
    },
    [onFileChange]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxSize: BULK_UPLOAD_MAX_FILE_SIZE_MB * 1024 * 1024,
    multiple: false,
    disabled,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <div className="space-y-3">
      <div
        {...getRootProps({
          className: cn(
            'rounded-xl border-2 border-dashed bg-accent/20 px-4 py-10 text-center transition-colors',
            isDragActive ? 'border-primary bg-primary/5' : 'border-input',
            disabled && 'cursor-not-allowed opacity-60'
          ),
        })}
      >
        <Input {...getInputProps()} />
        <div className="mx-auto flex max-w-md flex-col items-center gap-3">
          <div className="rounded-full bg-primary/10 p-3 text-primary">
            <UploadCloud className="size-6" />
          </div>
          <div className="space-y-1">
            <h3 className="font-semibold">
              Drag and drop your CSV or Excel file here
            </h3>
            <p className="text-sm text-muted-foreground">or</p>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={open}
          >
            Browse Files
          </Button>
          <p className="text-xs text-muted-foreground">
            Supports CSV and XLSX · Maximum file size{' '}
            {BULK_UPLOAD_MAX_FILE_SIZE_MB}
            MB
          </p>
        </div>
      </div>

      {file && (
        <div className="flex items-center justify-between rounded-lg border bg-white px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <FileSpreadsheet className="size-5 shrink-0 text-primary" />
            <div className="min-w-0 text-left">
              <p className="truncate text-sm font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Remove file"
              onClick={() => {
                setError(null);
                onFileChange(null);
              }}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      <p className="text-xs text-muted-foreground">
        Files up to {BULK_UPLOAD_MAX_FILE_SIZE_MB.toFixed(2)} MB are accepted.
      </p>
    </div>
  );
}
